import { useState, useEffect } from "react";
import {
  Typography, Grid2 as Grid, TextField, FormControl, Select, MenuItem, FormHelperText,
  InputLabel, Button, Container, ListSubheader
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getEstados, getEstadosEEUU } from "../../actions/estados";
import { isEmpty } from "../../components/common/helpers";
import { addEmpresa, getEmpresaById, updateEmpresa } from "../../actions/empresas";

const AddEmpresa = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { empresa, loading } = useSelector(state => state.empresas);

  const [newRegister, setNewRegister] = useState({
    razon: "",
    rfc: "",
    regimen: "",
    pais: "Mexico",
    estado: "",
    cp: "",
    delegacion: "",
    colonia: "",
    calle: "",
    numExt: "",
    numInt: "",
    email: "default@company.com",
    tel: "0000000000",
    ext: "52"
  });

  // Initialize with one empty service input
  const [servicios, setServicios] = useState([""]);

  const [errors, setErrors] = useState({});
  const [estados, setEstados] = useState([]);
  const [municipios, setMunicipios] = useState([]);

  useEffect(() => {
    if (id) {
      dispatch(getEmpresaById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && empresa && !loading) {
      setNewRegister({
        email: empresa.email || "default@company.com",
        ext: empresa.ext || "52",
        tel: empresa.tel || "0000000000",
        razon: empresa.razon || "",
        rfc: empresa.rfc || "",
        regimen: empresa.regimen || "",
        pais: empresa.pais || "Mexico",
        estado: empresa.estado || "",
        cp: empresa.cp || "",
        delegacion: empresa.delegacion || "",
        colonia: empresa.colonia || "",
        calle: empresa.calle || "",
        numExt: empresa.numExt || "",
        numInt: empresa.numInt || "",
      });
      // If backend provides services, set them here. 
      // Ensure there's at least one input if empty.
      if (empresa.servicios && Array.isArray(empresa.servicios) && empresa.servicios.length > 0) {
        setServicios(empresa.servicios);
      } else {
        setServicios([""]);
      }
    }
  }, [id, empresa, loading]);

  useEffect(() => {
    (async () => {
      try {
        if (newRegister.pais === "Mexico") {
          const arrEstados = await getEstados();
          setEstados(arrEstados);
        } else if (newRegister.pais === "EEUU") {
          const arrEstados = await getEstadosEEUU();
          setEstados(arrEstados);
        }
      } catch (err) {
        console.error("Error: ", err);
      }
    })();
  }, [newRegister.pais]);

  useEffect(() => {
    if (newRegister.pais === "Mexico" && !isEmpty(estados)) {
      const index = estados?.findIndex(edo => edo.estado === newRegister.estado);
      if (index !== -1 && estados[index]) {
        setMunicipios(estados[index].municipio);
      } else {
        setMunicipios([]);
      }
    } else {
      setMunicipios([]);
    }
  }, [newRegister.estado, estados, newRegister.pais]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewRegister(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleAddServiceInput = () => {
    setServicios([...servicios, ""]);
  };

  const handleServiceChange = (index, value) => {
    const newServicios = [...servicios];
    newServicios[index] = value;
    setServicios(newServicios);
  };

  const validate = () => {
    const tempErrors = {};
    if (!newRegister.razon) tempErrors.razon = "La razón social es requerida";
    if (!newRegister.rfc) tempErrors.rfc = "El RFC es requerido";
    if (!newRegister.regimen) tempErrors.regimen = "El régimen fiscal es requerido";
    if (!newRegister.pais) tempErrors.pais = "El país es requerido";
    if (!newRegister.estado) tempErrors.estado = "El estado es requerido";
    if (!newRegister.delegacion) tempErrors.delegacion = "El municipio/ciudad es requerido";
    if (!newRegister.cp) tempErrors.cp = "El código postal es requerido";
    if (!newRegister.colonia) tempErrors.colonia = "La colonia es requerida";
    if (!newRegister.calle) tempErrors.calle = "La calle es requerida";
    if (!newRegister.numExt) tempErrors.numExt = "El número exterior es requerido";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      // Filter out empty services
      const cleanServicios = servicios.filter(s => s.trim() !== "");

      const dataToSend = {
        ...newRegister,
        servicios: cleanServicios
      };

      if (id) {
        await dispatch(updateEmpresa(id, dataToSend));
      } else {
        await dispatch(addEmpresa(dataToSend));
      }
      navigate("/admin/empresas");
    } else {
      console.log("Validation Failed");
    }
  };

  const handleCancel = () => {
    navigate("/admin/empresas");
  };

  return (
    <Container component="main" maxWidth="md">
      <Grid container mt={{ xs: 10, md: 11 }} justifyContent="center">

        <Grid size={12} sx={{ mb: 4 }} mt={3}>
          <Typography variant="h4" component="h1" color="primary" sx={{ fontWeight: 'bold' }}>
            Datos de la empresa
          </Typography>
          <Typography variant="body1">
            Completa el formulario con la información de la empresa que invertirá en tu proyecto.
          </Typography>
        </Grid>

        <Grid size={12}>
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>

              {/* Razón social */}
              <Grid size={12}>
                <InputLabel sx={{ mb: 1, fontWeight: 'bold' }}>Razón social</InputLabel>
                <TextField
                  fullWidth
                  placeholder="Razón social del inversionista"
                  name="razon"
                  value={newRegister.razon}
                  onChange={handleChange}
                  error={!!errors.razon}
                  helperText={errors.razon}
                />
              </Grid>

              {/* RFC y Régimen */}
              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel sx={{ mb: 1, fontWeight: 'bold' }}>RFC</InputLabel>
                <TextField
                  fullWidth
                  placeholder="Clave del Registro Federal de Contribuyentes"
                  name="rfc"
                  value={newRegister.rfc}
                  onChange={handleChange}
                  error={!!errors.rfc}
                  helperText={errors.rfc}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel sx={{ mb: 1, fontWeight: 'bold' }}>Régimen Fiscal</InputLabel>
                <FormControl fullWidth error={!!errors.regimen}>
                  <Select
                    displayEmpty
                    name="regimen"
                    value={newRegister.regimen}
                    onChange={handleChange}
                  >
                    <MenuItem value=""><em>Selecciona</em></MenuItem>
                    <ListSubheader color="primary">Personas Físicas</ListSubheader>
                    <MenuItem value="1">Sueldos y Salarios e Ingresos Asimilados a Salarios</MenuItem>
                    <MenuItem value="2">Actividades Empresariales y Profesionales</MenuItem>
                    <MenuItem value="3">Régimen Simplificado de Confianza (RESICO-PF)</MenuItem>
                    <MenuItem value="4">Régimen de Incorporación Fiscal (RIF)*</MenuItem>
                    <MenuItem value="5">Enajenación de Bienes</MenuItem>
                    <MenuItem value="6">Adquisición de Bienes</MenuItem>
                    <MenuItem value="7">Arrendamiento (Uso o goce temporal de bienes)</MenuItem>
                    <MenuItem value="8">Intereses</MenuItem>
                    <MenuItem value="9">Obtención de Premios</MenuItem>
                    <MenuItem value="10">Dividendos y ganancias distribuidas</MenuItem>
                    <MenuItem value="11">Actividades Empresariales mediante Plataformas Tecnológicas</MenuItem>
                    <MenuItem value="12">Residentes en el Extranjero sin Establecimiento Permanente en México</MenuItem>
                    <MenuItem value="13">De los demás ingresos</MenuItem>
                    <ListSubheader color="primary">Personas Morales</ListSubheader>
                    <MenuItem value="14">Régimen General de Ley</MenuItem>
                    <MenuItem value="15">Personas Morales con Fines no Lucrativos</MenuItem>
                    <MenuItem value="16">Régimen Simplificado de Confianza (RESICO-PM)</MenuItem>
                    <MenuItem value="17">Grupos de Sociedades (Opcional)</MenuItem>
                    <MenuItem value="18">De los Coordinados</MenuItem>
                    <MenuItem value="19">Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras (AGASPES)</MenuItem>
                    <MenuItem value="20">Sociedades Cooperativas de Producción (Diferimiento de ingresos)</MenuItem>
                    <MenuItem value="21">Residentes en el Extranjero sin Establecimiento Permanente en México</MenuItem>
                  </Select>
                  {errors.regimen && <FormHelperText>{errors.regimen}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Dirección fiscal */}
              <Grid size={12} sx={{ mt: 2 }}>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>Dirección fiscal</Typography>
                <Typography variant="body2">Dirección fiscal registrada ante el SAT</Typography>
              </Grid>

              {/* País y Estado */}
              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel sx={{ mb: 1 }}>País</InputLabel>
                <FormControl fullWidth error={!!errors.pais}>
                  <Select
                    displayEmpty
                    name="pais"
                    value={newRegister.pais}
                    onChange={handleChange}
                  >
                    <MenuItem value=""><em>Selecciona</em></MenuItem>
                    <MenuItem value="Mexico">México</MenuItem>
                    <MenuItem value="EEUU">EEUU</MenuItem>
                  </Select>
                  {errors.pais && <FormHelperText>{errors.pais}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel sx={{ mb: 1 }}>Estado</InputLabel>
                <FormControl fullWidth error={!!errors.estado}>
                  <Select
                    displayEmpty
                    name="estado"
                    value={newRegister.estado}
                    onChange={handleChange}
                  >
                    <MenuItem value=""><em>Selecciona</em></MenuItem>
                    {newRegister.pais === "Mexico" && estados.map((e, i) => (
                      <MenuItem key={e.estado || i} value={e.estado}>{e.estado}</MenuItem>
                    ))}
                    {newRegister.pais === "EEUU" && estados.map((e, i) => (
                      <MenuItem key={e.name || i} value={e.name}>{e.name}</MenuItem>
                    ))}
                  </Select>
                  {errors.estado && <FormHelperText>{errors.estado}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* CP y Municipio */}
              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel sx={{ mb: 1 }}>Código Postal</InputLabel>
                <TextField
                  fullWidth
                  placeholder="C.P."
                  name="cp"
                  value={newRegister.cp}
                  onChange={handleChange}
                  error={!!errors.cp}
                  helperText={errors.cp}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel sx={{ mb: 1 }}>Delegación o Municipio</InputLabel>
                {newRegister.pais === "Mexico" ? (
                  <FormControl fullWidth error={!!errors.delegacion}>
                    <Select
                      displayEmpty
                      name="delegacion"
                      value={newRegister.delegacion}
                      onChange={handleChange}
                    >
                      <MenuItem value=""><em>Selecciona</em></MenuItem>
                      {municipios.map((m, i) => (
                        <MenuItem key={m || i} value={m}>{m}</MenuItem>
                      ))}
                    </Select>
                    {errors.delegacion && <FormHelperText>{errors.delegacion}</FormHelperText>}
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    placeholder="Ciudad"
                    name="delegacion"
                    value={newRegister.delegacion}
                    onChange={handleChange}
                    error={!!errors.delegacion}
                    helperText={errors.delegacion}
                  />
                )}
              </Grid>

              {/* Colonia and Calle */}
              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel sx={{ mb: 1 }}>Colonia</InputLabel>
                <TextField
                  fullWidth
                  placeholder="Selecciona"
                  name="colonia"
                  value={newRegister.colonia}
                  onChange={handleChange}
                  error={!!errors.colonia}
                  helperText={errors.colonia}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel sx={{ mb: 1 }}>Calle</InputLabel>
                <TextField
                  fullWidth
                  placeholder="Nombre de la calle"
                  name="calle"
                  value={newRegister.calle}
                  onChange={handleChange}
                  error={!!errors.calle}
                  helperText={errors.calle}
                />
              </Grid>

              {/* Numeros */}
              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel sx={{ mb: 1 }}>Número exterior</InputLabel>
                <TextField
                  fullWidth
                  placeholder="Num ext"
                  name="numExt"
                  value={newRegister.numExt}
                  onChange={handleChange}
                  error={!!errors.numExt}
                  helperText={errors.numExt}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel sx={{ mb: 1 }}>Número interior</InputLabel>
                <TextField
                  fullWidth
                  placeholder="Num int"
                  name="numInt"
                  value={newRegister.numInt}
                  onChange={handleChange}
                  helperText={<span style={{ color: '#888', fontSize: 13 }}>Opcional</span>}
                />
              </Grid>

              {/* Servicios de la empresa */}
              <Grid size={12}>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>Servicios de la empresa</Typography>
                <Typography variant="body2">Agrega los servicios de la empresa</Typography>
              </Grid>

              <Grid size={12}>
                <InputLabel sx={{ mb: 1 }}>Servicio</InputLabel>
                {servicios.map((servicio, index) => (
                  <Grid container key={index} sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="Escribe"
                      value={servicio}
                      onChange={(e) => handleServiceChange(index, e.target.value)}
                    />
                  </Grid>
                ))}
              </Grid>

              <Grid size={12}>
                <Button
                  onClick={handleAddServiceInput}
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                  + Añadir otro
                </Button>
              </Grid>


              {/* Botones */}
              <Grid size={12} mt={4} display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  sx={{ color: '#000', borderColor: '#ccc', textTransform: 'none' }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ textTransform: 'none', backgroundColor: '#1a237e' }}
                >
                  {servicios.filter(s => s.trim()).length > 0 ? "Guardar" : "Guardar sin servicios"}
                </Button>
              </Grid>

            </Grid>
          </form>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AddEmpresa;
